/**
 * Map an order_line's solution_subtype (PA12, carbon, sandal, etc.) to its
 * short product family name. For OTS / non-subtyped lines, fall back to the
 * joined `product.name_en` (e.g. "OTS EVA insole - small"), then to a
 * formatted line_type as a last resort.
 *
 * Kept as a plain function (not a hook) so it can be reused from non-React
 * contexts and inside i18n-aware callers via the `t` lookup table.
 */

import type { OrderListLine } from '@insolech/sdk';
import type { SolutionSubtype } from './wizard/solutionSubtypes';

type SubtypeShort =
  | 'pa12'
  | 'tpu'
  | 'polypropylene'
  | 'carbon'
  | 'eva'
  | 'fibreglass'
  | 'sandal'
  | 'refurbishment';

// The `satisfies` clause is the exhaustiveness guard: adding a member to
// SolutionSubtype without a label here is a COMPILE error. Order 2601010
// showed why it matters: the tracker used to have a parallel hand-written
// switch that covered 7 of the 8 subtypes, and the missing
// insoles_refurbishment case fell through to the linked catalog product
// ("Custom EVA insole").
const SUBTYPE_TO_SHORT = {
  pa12_insole: 'pa12',
  tpu_insole: 'tpu',
  polypropylene_insole: 'polypropylene',
  carbon_insole: 'carbon',
  eva_insole: 'eva',
  fibreglass_insole: 'fibreglass',
  orthopaedic_sandal: 'sandal',
  insoles_refurbishment: 'refurbishment',
} satisfies Record<SolutionSubtype, SubtypeShort>;

/** EN display labels for each short subtype key. */
const SHORT_LABEL: Record<SubtypeShort, string> = {
  pa12: 'PA12 insole',
  tpu: 'TPU insole',
  polypropylene: 'Polypropylene insole',
  carbon: 'Carbon fibre insole',
  eva: 'EVA insole',
  fibreglass: 'Fibreglass insole',
  sandal: 'Orthopaedic sandal',
  refurbishment: 'Insoles refurbishment',
};

interface LineLike {
  line_type?: string | null | undefined;
  solution_subtype?: string | null | undefined;
  product?: { name_en?: string | null } | null | undefined;
}

export function shortProductLabel(line: LineLike | null | undefined): string {
  if (!line) return '-';
  if (line.solution_subtype) {
    const label = shortProductLabelFromSubtype(line.solution_subtype);
    if (label) return label;
  }
  if (line.product?.name_en) return line.product.name_en;
  if (line.line_type) {
    return line.line_type.replace(/_/g, ' ');
  }
  return '-';
}

/** Line types that are NOT custom-made: ready-made insoles (ots_insole) and
 *  orthopaedic materials / FITO shoes (other_equipment). */
const NON_CUSTOM_LINE_TYPES = new Set(['ots_insole', 'other_equipment']);

/** Whether the line belongs to the Custom-made branch (vs. Off-the-shelf). */
export function isCustomLine(lineType: string | null | undefined): boolean {
  if (!lineType) return false;
  return !NON_CUSTOM_LINE_TYPES.has(lineType);
}

/** Human label for the wizard solution branch. */
export function solutionBranchLabel(lineType: string | null | undefined): string {
  return isCustomLine(lineType) ? 'Custom made' : 'Off-the-shelf';
}

interface CategoryLineLike {
  line_type?: string | null | undefined;
  configuration?: Record<string, unknown> | null | undefined;
}

/**
 * Catalog category label for a non-custom line, used as the order title when
 * there's no patient attached. Reads the wizard's recorded `sub_branch`, with
 * a line_type fallback for older orders.
 */
export function orderCategoryLabel(line: CategoryLineLike | null | undefined): string {
  const sub = line?.configuration?.['sub_branch'] as string | undefined;
  if (sub === 'ready_made') return 'Ready-made insoles';
  if (sub === 'orthopaedic_materials') return 'Orthopaedic materials';
  if (sub === 'fito_shoes') return 'FITO shoes';
  if (line?.line_type === 'ots_insole') return 'Ready-made insoles';
  if (line?.line_type === 'other_equipment') return 'Orthopaedic materials';
  return 'Off-the-shelf order';
}

/* -------------------------------------------------------------------------- */
/*  Tracker helpers (moved verbatim from OrderTrackerPage)                    */
/* -------------------------------------------------------------------------- */

function formatLineType(s: string | undefined): string {
  if (!s) return '-';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** configuration.material cache value -> product family short key. The label
 *  values are DERIVED from SHORT_LABEL below so the material-cache fallback
 *  can never disagree with the subtype-derived label; SHORT_LABEL is the one
 *  source for every product-family display string. */
const MATERIAL_TO_SHORT: Record<string, SubtypeShort> = {
  PA12: 'pa12',
  TPU: 'tpu',
  Polypropylene: 'polypropylene',
  PolyProp: 'polypropylene',
  EVA: 'eva',
  Carbon: 'carbon',
  Fibreglass: 'fibreglass',
};
export const CUSTOM_INSOLE_MATERIAL_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(MATERIAL_TO_SHORT).map(([material, short]) => [material, SHORT_LABEL[short]]),
);

/**
 * Task B4: concrete product label for the tracker's Product column.
 *
 * Walks the line's line_type + configuration JSONB and produces a short,
 * descriptive label that matches what the operator actually picked in the
 * wizard:
 *   - Custom insoles → "<material> insole" from configuration.material
 *     (PA12, EVA, PolyProp, Carbon, Fibreglass, TPU). Falls back to the
 *     subtype-derived label, then to the product name_en.
 *   - Sandals → "Orthopaedic sandal" (the variant adds nothing useful).
 *   - other_equipment (FITO shoes, ready-made, orthopaedic materials) →
 *     configuration.product_name first, then configuration.display_label,
 *     then product.name_en, then a formatted line_type.
 *   - ots_insole → configuration.display_label, then product.name_en.
 *   - Anything else → product.name_en, then a formatted line_type.
 */
export function formatProductLabel(line: OrderListLine | null | undefined): string {
  if (!line) return '-';

  const cfg = line.configuration ?? {};
  const material =
    typeof cfg['material'] === 'string' ? (cfg['material'] as string).trim() : '';
  const productName =
    typeof cfg['product_name'] === 'string' ? (cfg['product_name'] as string).trim() : '';
  const displayLabel =
    typeof cfg['display_label'] === 'string' ? (cfg['display_label'] as string).trim() : '';

  // Custom-made insole branches. premium_3d_insole = PA12; custom_insole =
  // everything else (TPU/EVA/PolyProp/Carbon/Fibreglass), both store the
  // shell material under configuration.material.
  if (line.line_type === 'custom_insole' || line.line_type === 'premium_3d_insole') {
    // Prefer solution_subtype over configuration.material: subtype is
    // the canonical truth, material is a denormalised cache that may
    // drift after an EditPrescriptionDialog save.
    if (line.solution_subtype) {
      const fallback = shortProductLabelFromSubtype(line.solution_subtype);
      if (fallback) return fallback;
    }
    if (material && CUSTOM_INSOLE_MATERIAL_LABELS[material]) {
      return CUSTOM_INSOLE_MATERIAL_LABELS[material];
    }
    if (line.product?.name_en) return line.product.name_en;
    return 'Custom insole';
  }

  if (line.line_type === 'orthopedic_sandal') {
    // Canonical spelling from SHORT_LABEL; the line_type enum itself stays
    // American 'orthopedic_sandal', only the display label is unified.
    return SHORT_LABEL.sandal;
  }

  // Marketplace / OTS branches store the wizard's selection inside the
  // configuration JSONB. Prefer the explicit product_name (e.g. "F1BK - Fito
  // Model 1 Black"); fall back to the display_label that includes size info.
  if (line.line_type === 'other_equipment') {
    if (productName) return productName;
    if (displayLabel) return displayLabel;
    if (line.product?.name_en) return line.product.name_en;
    return formatLineType(line.line_type);
  }

  if (line.line_type === 'ots_insole') {
    if (displayLabel) return displayLabel;
    if (productName) return productName;
    if (line.product?.name_en) return line.product.name_en;
    return formatLineType(line.line_type);
  }

  // Catch-all (sandal_insole_combo, legacy line types, etc.).
  if (line.product?.name_en) return line.product.name_en;
  return formatLineType(line.line_type);
}

/**
 * Subtype -> display label via the single canonical map (SUBTYPE_TO_SHORT +
 * SHORT_LABEL). Used by both the tracker (formatProductLabel) and the detail
 * page (shortProductLabel) so the two surfaces can never disagree again.
 */
export function shortProductLabelFromSubtype(subtype: string): string | null {
  const short = (SUBTYPE_TO_SHORT as Record<string, SubtypeShort | undefined>)[subtype];
  return short ? SHORT_LABEL[short] : null;
}

export interface PatientDisplay {
  name: string | null;
  /** True when the resolved name came from a line.patient_name fallback. */
  isLinePatient: boolean;
  /** Extra lines that also had different patient_name values. */
  extraCount: number;
}

/**
 * Minimal shape resolvePatientDisplay needs. Both the tracker's OrderListRow
 * and the detail page's OrderDetail satisfy it structurally, so the helper can
 * be reused on either without coupling to one row type.
 */
export interface PatientDisplaySource {
  patient?: { full_name?: string | null } | null;
  lines?: ReadonlyArray<{ patient_name?: string | null }> | null;
}

/**
 * Task B5: resolve the Patient-column display for an order.
 *
 * Custom flows attach a real patient to the order header; marketplace flows
 * (fito_shoes, ready_made, orthopaedic_materials) AND record-less reorders
 * (reorder-by-name, plus every migrated order) carry a free-text patient name
 * on the line instead. When the order has no header patient AND any line
 * carries patient_name, surface that line's name with a small disambiguator.
 * Multiple distinct names collapse into "<first> +N more".
 */
export function resolvePatientDisplay(row: PatientDisplaySource): PatientDisplay {
  // An explicit per-order line name (order_lines.patient_name) is an
  // ORDER-SCOPED OVERRIDE and wins over the shared header patient record. This
  // is what makes an in-context name edit (update_order_patient_name) show up
  // on the tracker/detail/dialog/Excel even when the shared patients record was
  // deliberately left unchanged (shared or foreign-clinic record). Multiple
  // distinct line names collapse into "<first> +N more". Falls back to the
  // header record, then nothing.
  const names: string[] = [];
  for (const l of row.lines ?? []) {
    const n = (l.patient_name ?? '').trim();
    if (n && !names.includes(n)) names.push(n);
  }
  if (names.length > 0) {
    return {
      name: names[0] ?? null,
      isLinePatient: true,
      extraCount: Math.max(0, names.length - 1),
    };
  }
  if (row.patient?.full_name) {
    return { name: row.patient.full_name, isLinePatient: false, extraCount: 0 };
  }
  return { name: null, isLinePatient: false, extraCount: 0 };
}
